/**
 * /admin/access-health — Health Dashboard системы доступов RLS.
 *
 * Показывает:
 * - Карточки синхронизации (PG→CH, CH views, последний деплой)
 * - Таблицу ЛПУ с количеством пользователей и CH-строк
 */

import {
  Breadcrumb,
  Button,
  Card,
  Col,
  Row,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Alert,
  Spin,
  Statistic,
} from 'antd'
import {
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  SafetyOutlined,
  TableOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import 'dayjs/locale/ru'
import { useNavigate } from 'react-router-dom'

import { useAccessHealth } from '@/hooks/useAccessHealth'
import type {
  BiAuditEntryIssue,
  BiAuditUserIssue,
  LpuHealthItem,
  SyncCheckItem,
} from '@/api/accessHealth'

dayjs.locale('ru')

const { Title, Text } = Typography

// ── Status helpers ────────────────────────────────────────────────────────────

function statusIcon(status: string) {
  switch (status) {
    case 'ok':
      return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />
    case 'warning':
      return <WarningOutlined style={{ color: '#faad14', fontSize: 18 }} />
    case 'error':
      return <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
    case 'info':
    default:
      return <InfoCircleOutlined style={{ color: '#1677ff', fontSize: 18 }} />
  }
}

function statusTag(status: string) {
  switch (status) {
    case 'ok':
      return <Tag color="success">✅ OK</Tag>
    case 'warning':
      return <Tag color="warning">⚠️ Внимание</Tag>
    case 'error':
      return <Tag color="error">❌ Ошибка</Tag>
    case 'info':
      return <Tag color="processing">ℹ Информация</Tag>
    default:
      return <Tag color="default">{status}</Tag>
  }
}

function biIssueLabel(issue: string) {
  switch (issue) {
    case 'direct_grant_outside_user_org':
      return 'Grant вне org'
    case 'direct_grant_without_org_scope':
      return 'Grant без org'
    case 'visible_bi_without_pg_rls_source':
      return 'BI без PG RLS'
    default:
      return issue
  }
}

function renderOrgTags(orgs: BiAuditEntryIssue['orgs']) {
  if (!orgs.length) {
    return <Tag color="warning">нет org</Tag>
  }
  return orgs.map((org) => (
    <Tooltip key={org.id} title={org.name}>
      <Tag>{org.code}</Tag>
    </Tooltip>
  ))
}

// ── Sync check card ───────────────────────────────────────────────────────────

function SyncCheckCard({ check }: { check: SyncCheckItem }) {
  const borderColor =
    check.status === 'ok'
      ? '#52c41a'
      : check.status === 'warning'
        ? '#faad14'
        : check.status === 'error'
          ? '#ff4d4f'
          : '#1677ff'

  return (
    <Card
      size="small"
      style={{ borderLeft: `4px solid ${borderColor}` }}
    >
      <Space align="start">
        {statusIcon(check.status)}
        <div>
          <Text strong>{check.label}</Text>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {check.message}
            </Text>
          </div>
        </div>
      </Space>
    </Card>
  )
}

// ── Main page component ───────────────────────────────────────────────────────

export default function AccessHealthPage() {
  const { data, isLoading, isError, error, refetch } = useAccessHealth()
  const navigate = useNavigate()

  const handleRefresh = () => {
    refetch()
  }

  const biEntryColumns: ColumnsType<BiAuditEntryIssue> = [
    {
      title: 'Entry',
      dataIndex: 'datalens_entry_id',
      key: 'datalens_entry_id',
      width: 150,
      render: (v: string, record) => (
        <Space direction="vertical" size={0}>
          <Text code>{v}</Text>
          {record.entry_title ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.entry_title}
            </Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Организации',
      dataIndex: 'orgs',
      key: 'orgs',
      width: 180,
      render: renderOrgTags,
    },
    {
      title: 'Источники',
      dataIndex: 'sources',
      key: 'sources',
      render: (sources: string[]) => sources.map((source) => (
        <Tag key={source} color="blue">
          {source}
        </Tag>
      )),
    },
  ]

  const biUserIssueColumns: ColumnsType<BiAuditUserIssue> = [
    {
      title: 'Пользователь',
      dataIndex: 'email',
      key: 'email',
      width: 220,
      render: (email: string, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.full_name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {email}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Org',
      dataIndex: 'org',
      key: 'org',
      width: 90,
      render: (org: BiAuditUserIssue['org']) => (
        org ? <Tooltip title={org.name}><Tag>{org.code}</Tag></Tooltip> : <Tag color="warning">нет</Tag>
      ),
    },
    {
      title: 'Проблема',
      dataIndex: 'issue',
      key: 'issue',
      width: 150,
      render: (issue: string) => <Tag color="warning">{biIssueLabel(issue)}</Tag>,
    },
    {
      title: 'Entry / детали',
      key: 'details',
      render: (_: unknown, record) => (
        <Space direction="vertical" size={2}>
          {record.datalens_entry_id ? (
            <Text code>{record.datalens_entry_id}</Text>
          ) : (
            <Text type="secondary">
              Видимых entry: {record.visible_entry_count ?? '—'}
            </Text>
          )}
          {record.entry_title ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.entry_title}
            </Text>
          ) : null}
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.details}
          </Text>
          {record.source_groups.length ? (
            <div>
              {record.source_groups.map((group) => (
                <Tag key={group}>{group}</Tag>
              ))}
            </div>
          ) : null}
        </Space>
      ),
    },
  ]

  // ── LPU table columns ────────────────────────────────────────────────────

  const lpuColumns: ColumnsType<LpuHealthItem> = [
    {
      title: 'Код',
      dataIndex: 'code',
      key: 'code',
      width: 90,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
    },
    {
      title: 'Польз. (PG)',
      dataIndex: 'pg_users',
      key: 'pg_users',
      width: 110,
      align: 'right',
      sorter: (a, b) => a.pg_users - b.pg_users,
      render: (v: number) => <Text>{v}</Text>,
    },
    {
      title: 'CH строк',
      dataIndex: 'ch_rows',
      key: 'ch_rows',
      width: 100,
      align: 'right',
      sorter: (a, b) => a.ch_rows - b.ch_rows,
      render: (v: number) => <Text>{v}</Text>,
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      filters: [
        { text: '✅ OK', value: 'ok' },
        { text: '⚠️ Внимание', value: 'warning' },
        { text: '❌ Ошибка', value: 'error' },
        { text: 'ℹ Информация', value: 'info' },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status: string) => statusTag(status),
    },
    {
      title: 'Примечание',
      dataIndex: 'note',
      key: 'note',
      ellipsis: true,
      render: (note: string | null) =>
        note ? (
          <Tooltip title={note}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {note}
            </Text>
          </Tooltip>
        ) : (
          '—'
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_: unknown, record: LpuHealthItem) => (
        <Button
          size="small"
          icon={<TableOutlined />}
          disabled={record.status === 'info' || record.code === '000000'}
          onClick={() =>
            navigate(`/admin/lpu/${record.code}/access-matrix`)
          }
        >
          Матрица
        </Button>
      ),
    },
  ]

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div>
      <Breadcrumb
        items={[
          { title: 'Администрирование' },
          { title: 'Здоровье системы доступов' },
        ]}
        style={{ marginBottom: 16 }}
      />

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
        }}
      >
        <Space>
          <SafetyOutlined style={{ fontSize: 20 }} />
          <Title level={4} style={{ margin: 0 }}>
            Здоровье системы доступов
          </Title>
        </Space>
        <Button
          icon={<ReloadOutlined spin={isLoading} />}
          onClick={handleRefresh}
          loading={isLoading}
        >
          Обновить
        </Button>
      </div>

      {/* Ошибка загрузки */}
      {isError && (
        <Alert
          type="error"
          showIcon
          message="Ошибка загрузки данных"
          description={
            (error as any)?.response?.data?.detail ||
            (error as Error)?.message ||
            'Неизвестная ошибка'
          }
          style={{ marginBottom: 16 }}
        />
      )}

      {isLoading && !data ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" tip="Проверяем состояние системы..." />
        </div>
      ) : (
        <>
          {/* Блок: Синхронизация */}
          <div style={{ marginBottom: 24 }}>
            <Title level={5} style={{ marginBottom: 12 }}>
              Синхронизация
            </Title>
            <Row gutter={[12, 12]}>
              {(data?.sync_checks ?? []).map((check) => (
                <Col key={check.name} xs={24} sm={12} lg={8}>
                  <SyncCheckCard check={check} />
                </Col>
              ))}
            </Row>
          </div>

          {/* Блок: BI object ACL */}
          {data?.bi_audit ? (
            <div style={{ marginBottom: 24 }}>
              <Title level={5} style={{ marginBottom: 12 }}>
                BI object ACL / org-scope
              </Title>

              <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                <Col xs={12} md={6}>
                  <Card size="small">
                    <Statistic title="Known entries" value={data.bi_audit.summary.known_entries} />
                  </Card>
                </Col>
                <Col xs={12} md={6}>
                  <Card size="small">
                    <Statistic
                      title="Без org-scope"
                      value={data.bi_audit.summary.orphan_entries}
                      valueStyle={{ color: data.bi_audit.summary.orphan_entries ? '#faad14' : undefined }}
                    />
                  </Card>
                </Col>
                <Col xs={12} md={6}>
                  <Card size="small">
                    <Statistic
                      title="Multi-org"
                      value={data.bi_audit.summary.multi_org_entries}
                      valueStyle={{ color: data.bi_audit.summary.multi_org_entries ? '#faad14' : undefined }}
                    />
                  </Card>
                </Col>
                <Col xs={12} md={6}>
                  <Card size="small">
                    <Statistic
                      title="BI без PG RLS"
                      value={data.bi_audit.summary.users_with_visible_bi_without_pg_rls}
                      valueStyle={{ color: data.bi_audit.summary.users_with_visible_bi_without_pg_rls ? '#ff4d4f' : undefined }}
                    />
                  </Card>
                </Col>
              </Row>

              {data.bi_audit.summary.truncated ? (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="Список проблем обрезан до первых 100 записей в каждом блоке. Сводные счетчики показывают полный объем."
                />
              ) : null}

              <Row gutter={[12, 12]}>
                <Col xs={24} xl={12}>
                  <Card
                    size="small"
                    title={`BI entry без org-scope (${data.bi_audit.summary.orphan_entries})`}
                  >
                    <Table
                      dataSource={data.bi_audit.orphan_entries}
                      columns={biEntryColumns}
                      rowKey="datalens_entry_id"
                      size="small"
                      pagination={{ pageSize: 5, hideOnSinglePage: true }}
                      locale={{ emptyText: 'Не найдено' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} xl={12}>
                  <Card
                    size="small"
                    title={`BI entry в нескольких org (${data.bi_audit.summary.multi_org_entries})`}
                  >
                    <Table
                      dataSource={data.bi_audit.multi_org_entries}
                      columns={biEntryColumns}
                      rowKey="datalens_entry_id"
                      size="small"
                      pagination={{ pageSize: 5, hideOnSinglePage: true }}
                      locale={{ emptyText: 'Не найдено' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} xl={12}>
                  <Card
                    size="small"
                    title={`Direct grant риски (${data.bi_audit.direct_grant_issues.length})`}
                  >
                    <Table
                      dataSource={data.bi_audit.direct_grant_issues}
                      columns={biUserIssueColumns}
                      rowKey={(row) => `${row.user_id}-${row.datalens_entry_id}-${row.issue}`}
                      size="small"
                      pagination={{ pageSize: 5, hideOnSinglePage: true }}
                      locale={{ emptyText: 'Не найдено' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} xl={12}>
                  <Card
                    size="small"
                    title={`Видимый BI без PG RLS (${data.bi_audit.visible_without_pg_rls.length})`}
                  >
                    <Table
                      dataSource={data.bi_audit.visible_without_pg_rls}
                      columns={biUserIssueColumns}
                      rowKey={(row) => `${row.user_id}-${row.issue}`}
                      size="small"
                      pagination={{ pageSize: 5, hideOnSinglePage: true }}
                      locale={{ emptyText: 'Не найдено' }}
                    />
                  </Card>
                </Col>
              </Row>
            </div>
          ) : null}

          {/* Блок: RLS по ЛПУ */}
          <div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
              }}
            >
              <Title level={5} style={{ margin: 0 }}>
                RLS по ЛПУ
              </Title>
              {data && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Проверено:{' '}
                  {dayjs(data.checked_at).format('DD.MM.YYYY HH:mm')}
                </Text>
              )}
            </div>

            <Alert
              type="info"
              showIcon={false}
              style={{ marginBottom: 12, fontSize: 12 }}
              message={
                <Text style={{ fontSize: 12 }}>
                  <strong>❌</strong> = userid-RLS вернёт пустые дашборды для
                  non-admin пользователей &nbsp;·&nbsp;
                  <strong>⚠️</strong> = нет данных (ожидается настройка)
                </Text>
              }
            />

            <Table
              dataSource={data?.lpu_health ?? []}
              columns={lpuColumns}
              rowKey="code"
              loading={isLoading}
              size="middle"
              pagination={false}
              locale={{ emptyText: 'Нет данных об организациях' }}
              rowClassName={(record) =>
                record.status === 'error'
                  ? 'ant-table-row-danger'
                  : record.status === 'warning'
                    ? 'ant-table-row-warning'
                    : ''
              }
            />
          </div>
        </>
      )}
    </div>
  )
}
